import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
  cancelledItemsStateSelector,
  setActiveDeliveryId,
  setScannedItemId,
  upsertCancelledItem,
} from "../../../lib/store/reducers/cancelledItemReducer";
import OrderInformation from "../OrderInformation/OrderInformation";
import WarningList from "../Sidebar/WarningList/WarningList";
import PickActions from "../PickActions/PickActions";
import CancelOrderButton from "../buttons/CancelOrderButton";
import Sidebar from "../Sidebar/Sidebar";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../lib/store/storeConfig";
import {
  currentBoxSelector,
  refreshConveyorBox,
} from "../../../lib/store/reducers/conveyorBoxReviewReducer";
import { addMessage, MessageType } from "../../../lib/store/reducers/responseMessagesReducer";
import { BOX_ITEM_VERIFICATION_ERRORS, SHELF_SCAN_ERRORS } from "../../../utils/Constants";
import { getItemByIdentifier } from "../../../utils/helpers/conveyor-box";
import { detectBarcodeType, isSatisfied } from "../../../utils/helpers/validation";
import { assignPrefix } from "../../../utils/helpers/barcode";
import ConveyorBoxClient, {
  ICancelledItemRestockRequest,
} from "../../../lib/conveyorbox/ConveyorBoxClient";
import { Message } from "semantic-ui-react";
import BarcodeModalButton from "../buttons/BarcodeModalButton";
import { useBarcodeScanner } from "../../../hooks/useBarcodeScanner";
import BarcodeScanner from "../BarcodeInput/BarcodeScanner";
import CancelledBoxItem from "../BoxItem/CancelledBoxItem";
import { triggerOrderOverviewStage } from "../../../lib/store/reducers/globalStateReducer";

const CancelledItemsOverview = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { items, scannedItemId, activeDeliveryId } = useSelector(cancelledItemsStateSelector);
  const { boxBarcode } = useSelector(currentBoxSelector)!;

  const { onExecuteHigherOrderHandlerOrDefault } = useBarcodeScanner();

  const onScanCancelledItem = async (scannedBarcode: string) => {
    if (scannedItemId) {
      dispatch(
        addMessage({
          messageType: MessageType.WARNING,
          title: "Hoppsan!",
          content: BOX_ITEM_VERIFICATION_ERRORS.FORCE_CONTINUE_WITH_SCANNED_ITEM,
        })
      );
      return;
    }

    const scannedItem = getItemByIdentifier(items, scannedBarcode);

    if (!scannedItem) {
      dispatch(
        addMessage({
          messageType: MessageType.WARNING,
          title: "Hoppsan!",
          content: BOX_ITEM_VERIFICATION_ERRORS.ITEM_NOT_IN_ORDER,
        })
      );
      return;
    }

    const { cancelledQuantity, cancelledQuantityVerified } = scannedItem;
    if (isSatisfied(cancelledQuantity, cancelledQuantityVerified)) {
      dispatch(
        addMessage({
          messageType: MessageType.WARNING,
          title: "Hoppsan!",
          content: BOX_ITEM_VERIFICATION_ERRORS.MAX_VERIFIED_REACHED(scannedBarcode),
        })
      );
    } else {
      const scannedItemUpdated = { ...scannedItem };
      scannedItemUpdated.cancelledQuantityVerified += 1;
      dispatch(upsertCancelledItem(scannedItemUpdated));
    }
  };

  const onScanShelf = async (scannedBarcode: string) => {
    const formattedBarcode = assignPrefix(scannedBarcode, "H").toUpperCase();
    const barcodeFormat = detectBarcodeType(formattedBarcode);
    if (barcodeFormat !== "SHELF_NUMBER") {
      dispatch(
        addMessage({
          messageType: MessageType.WARNING,
          title: "Hoppsan!",
          content: SHELF_SCAN_ERRORS.INCORRECT_TYPE,
        })
      );
      return;
    }
    const currentItem = items.find((i) => i.id === scannedItemId);
    if (!currentItem) return;

    const payload: ICancelledItemRestockRequest = {
      deliveryId: activeDeliveryId,
      itemId: currentItem.id,
      scannedQuantity: currentItem.cancelledQuantityVerified,
      missingQuantity: 0, // not used yet
      operatorAssignedShelf: formattedBarcode,
    };
    try {
      const res = await ConveyorBoxClient.restockCancelledRx(boxBarcode, payload);
      const { isValid, requestedShelf, suggestedShelf, deliveryId } = res;
      if (!isValid) {
        if (!suggestedShelf) {
          dispatch(
            addMessage({
              messageType: MessageType.WARNING,
              title: "Hoppsan!",
              content: "Något gick fel..",
            })
          );
          return;
        }
        dispatch(
          addMessage({
            messageType: MessageType.WARNING,
            title: "Hoppsan!",
            content: `Ogiltig hylla '${requestedShelf}'. Systemet rekommenderar hylla '${suggestedShelf}'`,
          })
        );
        return;
      }
      const updatedCurrentItem = {
        ...currentItem,
      };
      updatedCurrentItem.cancelledQuantity -= updatedCurrentItem.cancelledQuantityVerified;
      if (updatedCurrentItem.cancelledQuantity <= 0) {
        updatedCurrentItem.isRestocked = true;
        updatedCurrentItem.operatorAssignedShelf = requestedShelf;

        dispatch(setScannedItemId(null));
      }
      dispatch(upsertCancelledItem(updatedCurrentItem));
      dispatch(setActiveDeliveryId(deliveryId));
    } catch (error: any) {
      dispatch(
        addMessage({
          messageType: MessageType.WARNING,
          title: "Hoppsan!",
          content: error.message,
        })
      );
    }
  };

  useEffect(() => {
    if (items.some((i) => !i.isRestocked)) return;
    dispatch(refreshConveyorBox({ callbackFn: () => dispatch(triggerOrderOverviewStage()) }));
  }, [items]);

  const sidebarTitle = scannedItemId ? "Skanna hylla" : "Manuell hantering";
  const sidebarText = scannedItemId
    ? `Skanna den hylla som du vill placera, eller har placerat artikeln på.`
    : "En eller flera artiklar har blivit modifierade/avbrutna i LRX. Vänligen hantera dessa som lagerback.";

  return (
    <>
      <main className="control-app__content">
        {items.map((item) => (
          <CancelledBoxItem item={item} isSelected={item.id === scannedItemId} />
        ))}
      </main>
      <Sidebar show>
        <OrderInformation />
        <WarningList />
        <Message
          icon="exclamation"
          negative
          size="big"
          color={scannedItemId && scannedItemId > -1 ? "blue" : undefined}
          header={sidebarTitle}
          content={sidebarText}
        />
        <PickActions>
          {scannedItemId ? (
            <>
              <BarcodeModalButton
                onBarcodeScanAsync={onScanShelf}
                title="Ange hyllplats"
                textFieldLabel="Hyllplats"
              />
              <BarcodeScanner
                onScan={(barcode, format) =>
                  onExecuteHigherOrderHandlerOrDefault(barcode, format, onScanShelf)
                }
              />
            </>
          ) : (
            <>
              <BarcodeModalButton
                onBarcodeScanAsync={onScanCancelledItem}
                title="Ange varunummer"
                textFieldLabel="Varunummer"
              />

              <BarcodeScanner
                onScan={(barcode, format) =>
                  onExecuteHigherOrderHandlerOrDefault(barcode, format, onScanCancelledItem)
                }
              />
            </>
          )}
          <CancelOrderButton />
        </PickActions>
      </Sidebar>
    </>
  );
};

export default CancelledItemsOverview;
